import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Helmet } from "react-helmet";
import { useParams } from "react-router";
import { themes } from "style";
import { FRONTEND_EVENT_KEYS, FRONTEND_EVENTABLE_TYPES } from "constants/index";
import {
  useMissionRoleApplicant,
  usePublicMissionRoleApplicant,
  useMissionRoleApplicantAccess,
  useAssessment,
  useLogFrontendEvents,
} from "hooks";
import App from "App";
import { Grid, NarrowContainer } from "components/Containers";
import Card from "components/Card";
import { ProfileBox, ProfileCodeChallengeList, ProfileVideoInterviewsList } from "components/Profile";
import { Header } from "components/DisplayBox";
import { H2, H3 } from "components/Text";
import {
  ApplicantPdfDownloadLink,
  ApplicantPINModalForm,
  ApplicationQuestionnaireResponses,
} from "components/Missions/Applicants";
import { Spinner } from "components/Loader";
import { PublicClientHeader } from "components/Public";

/**
 * MissionRoleApplicantPreview
 *
 * @returns {JSX.Element} - Renders the mission role applicant preview page.
 */
const MissionRoleApplicantPreview = () => {
  const intl = useIntl();
  const { uuid } = useParams();
  const { userLoading, setAccessData, requiresPin } = useMissionRoleApplicantAccess({
    applicationUuid: uuid,
  });

  const { data: publicApplicant, loading: publicApplicantLoading } = usePublicMissionRoleApplicant({
    id: uuid,
    skip: !requiresPin,
  });

  const { data, loading } = useMissionRoleApplicant({
    id: uuid,
    skip: requiresPin,
    skipCountQuery: true,
  });

  const { data: assessment, loading: assessmentLoading } = useAssessment({
    id: data?.missionRole?.assessmentId,
    resourceId: data?.profile?.id,
  });

  // Track applicant viewed event for analytics
  useLogFrontendEvents({
    eventableId: data?.id,
    eventableType: FRONTEND_EVENTABLE_TYPES.missionRoleApplicant,
    key: FRONTEND_EVENT_KEYS.missionRoleApplicantPublicViewed,
    payload: {
      referrer: document.referrer,
    },
  });

  if (userLoading) return <Spinner />;

  if (requiresPin) {
    return (
      <ApplicantPINModalForm
        applicant={publicApplicant}
        loading={publicApplicantLoading}
        show
        onCompleted={setAccessData}
      />
    );
  }

  return (
    <App theme={themes.website}>
      <Helmet>
        <title>{intl.formatMessage({ id: "Meta.Missions.Applications.Title" })}</title>
      </Helmet>
      <PublicClientHeader />
      <NarrowContainer>
        <Grid topMargin bottomMargin>
          <Grid.col start={2} end={12}>
            <Header>
              <H2 loading={loading}>
                <FormattedMessage
                  id="Missions.Applications.PreviewPage.Header"
                  values={{
                    mission: data?.missionRole?.mission?.name,
                    role: data?.missionRole?.name,
                  }}
                />
              </H2>
              {data && (
                <H3>
                  <ApplicantPdfDownloadLink applicant={data} />
                </H3>
              )}
            </Header>
            <ProfileBox
              loading={loading}
              id={data?.profile?.uuid}
              isEditable={false}
              isPublicProfile={true}
              keyName="publicProfile"
              rate={data?.negotiatedSellingRate}
              showRate={!!data?.negotiatedSellingRate?.amount}
              showAiSummary
            />
          </Grid.col>
          <Grid.col start={2} end={12}>
            <Card>
              <Header>
                <H3 loading={loading}>
                  <FormattedMessage id="Missions.Applications.PreviewPage.Q&A" />
                </H3>
              </Header>
              <ApplicationQuestionnaireResponses
                loading={assessmentLoading || loading}
                data={data}
                assessment={assessment}
              />
            </Card>
          </Grid.col>
          <Grid.col start={2} end={12}>
            <Card>
              <Header>
                <H3 loading={loading}>
                  <FormattedMessage id="Missions.Applications.PreviewPage.CodeChallenges" />
                </H3>
              </Header>
              <ProfileCodeChallengeList profileId={data?.profile?.uuid} skip={!data?.profile?.uuid || loading} />
            </Card>
          </Grid.col>
          <Grid.col start={2} end={12}>
            <Card>
              <Header>
                <H3 loading={loading}>
                  <FormattedMessage id="Missions.Applicants.PreviewPage.Interviews" />
                </H3>
              </Header>
              <ProfileVideoInterviewsList
                profileId={data?.profile?.uuid}
                missionRoleId={data?.missionRole?.id}
                skip={!data?.profile?.uuid || loading}
                skipProfileQuery
              />
            </Card>
          </Grid.col>
        </Grid>
      </NarrowContainer>
    </App>
  );
};

export default MissionRoleApplicantPreview;
